/**
 * 需要抑制計画マスタ 取引先BG 編集 UI (調達/販売)。
 *
 * F5 リファクタ: 旧来は side prop で parameterize した 2 instance をレンダーしていた
 * (RHF anti-pattern: 同一 fieldArray 名 'partnerBgs' に対して useFieldArray が 2 つ)。
 * 1 component が PROCUREMENT/SALES 両 side を内部レンダーし useFieldArray を 1 つのみ所有する形に統合。
 *
 * 候補 filter = エリア (全区分)。区分フィルタは集約 RPC (BFF) が完了しているため、
 * client-side では area narrow のみ行う。
 * 既選択は除外 (同じ partnerBgs リスト内での重複を防ぐ)。
 *
 * mock-app OcctoMaster.razor 1370-1430 準拠。
 */
import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { DemandSuppressionPartnerSide } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  type Control,
  type FieldValues,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  OutlineDangerButton,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import { availableOptions } from '~/pages/AdminOcctoMaster/tabs/_common/models/availableOptions'
import {
  planMasterRowDefaultValidFrom,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type PartnerBgEntry = {
  side: number
  bgMasterId: string
  validFrom: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** i18n ページキー namespace */
  i18nNs: 'adminOcctoMaster.demandSuppressionPlanMaster'
  /**
   * 取引先BG 候補一覧 (集約 RPC が全区分を返す)。
   * client-side では area narrow のみ行う。
   */
  partnerBgs: BgMasterRow[]
  /**
   * 全 BG マスタ一覧 (削除済み判定専用、4526-plan-master-valid-period-filter
   * 設計書 §4)。 「(削除済み)」はこの一覧に存在しない場合のみ表示し、有効期間外で
   * partnerBgs 候補から外れただけの実在 BG は `{bgCode} - {bgName}` の
   * fallback 表示を維持する (AC-06)。 list クエリ取得中は undefined
   * (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。
   */
  allBgMasters: BgMasterRow[] | undefined
  /** 自身のエリアコード (同一エリアの BG のみ候補に絞り込む) */
  selfAreaCode: AreaCode | undefined
}

/**
 * 特定 side の 1 セクション (ヘッダ + 行リスト + 追加ボタン) をレンダーする内部 component。
 * useFieldArray は親 SuppressionPartnerBgsSection が 1 つだけ所有する。
 */
const SideSection = ({
  control,
  i18nNs,
  bgCandidates,
  allBgMasters,
  watchedRecordValidFrom,
  side,
  fields,
  watchedPartnerBgs,
  append,
  remove,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  i18nNs: 'adminOcctoMaster.demandSuppressionPlanMaster'
  bgCandidates: BgMasterRow[]
  allBgMasters: BgMasterRow[] | undefined
  /** form 全体の validFrom (= レコードの適用開始日、fallback の「（終了）」判定に使う)。 */
  watchedRecordValidFrom: CivilDate | undefined
  side:
    | DemandSuppressionPartnerSide.PROCUREMENT
    | DemandSuppressionPartnerSide.SALES
  fields: Array<{ id: string }>
  watchedPartnerBgs: PartnerBgEntry[]
  append: (value: PartnerBgEntry) => void
  remove: (index: number) => void
}): React.ReactElement => {
  const { t } = useTranslation()

  // 行ごとの select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない)。
  const { errors } = useFormState({ control })
  const partnerBgErrors = errors.partnerBgs as
    | Array<{ bgMasterId?: { message?: string } } | undefined>
    | undefined

  // この side に属するエントリの field index 一覧
  const sideIndices = fields
    .map((_, i) => i)
    .filter((i) => {
      const entry = watchedPartnerBgs[i]
      return (entry?.side as DemandSuppressionPartnerSide | undefined) === side
    })

  const noCandidates = bgCandidates.length === 0

  const sectionLabelKey =
    side === DemandSuppressionPartnerSide.PROCUREMENT
      ? `page:${i18nNs}.form.partnerBgsProcurement`
      : `page:${i18nNs}.form.partnerBgsSales`

  const addButtonKey =
    side === DemandSuppressionPartnerSide.PROCUREMENT
      ? `page:${i18nNs}.form.addPartnerBgProcurement`
      : `page:${i18nNs}.form.addPartnerBgSales`

  const emptyLabelKey =
    side === DemandSuppressionPartnerSide.PROCUREMENT
      ? `page:${i18nNs}.form.noPartnerBgsProcurementRegistered`
      : `page:${i18nNs}.form.noPartnerBgsSalesRegistered`

  const removeButtonKey = `page:${i18nNs}.form.removePartnerBg`

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 追加ボタン */}
      <HStack align="center" justify="space-between" marginBottom={2}>
        {/* design admin/occto.html `.occto-sub-label` = lg/700 (16px) が正 (#3735)。
            modal 内 H2 相当は `modalSectionTitle` を使う。 */}
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(sectionLabelKey)}
        </Text>
        <ButtonTooltip
          content={
            noCandidates ? t(`page:${i18nNs}.form.noPartnerBgCandidates`) : ''
          }
        >
          <TertiaryButton
            disabled={noCandidates}
            onClick={() => {
              append({
                side,
                bgMasterId: '',
                validFrom: planMasterRowDefaultValidFrom(
                  watchedRecordValidFrom,
                ),
                validTo: undefined,
              })
            }}
            /* [design準拠] アイコンを付けるので size は sm 以上にする
               (design css:「.btn-xs (高さ24px) はアイコン付きに向かないので、
               アイコンを付けるなら .btn-sm 以上を使う」)。 */
            size="sm"
            title={
              noCandidates ? t(`page:${i18nNs}.form.noPartnerBgCandidates`) : ''
            }
          >
            {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                揃わず、 翻訳のたびに記号を書き写す必要もあった。
                design css `.btn:not(...)>svg` = 20px。 */}
            <FaPlusRegular boxSize={iconSize.labeledButton} />
            {t(addButtonKey)}
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>

      {/* この side のエントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {sideIndices.length === 0 && (
          <Text color="Text.lowEmphasis" fontSize="sm">
            {t(emptyLabelKey)}
          </Text>
        )}
        {sideIndices.map((idx) => {
          const entry: PartnerBgEntry = watchedPartnerBgs[idx] ?? {
            side,
            bgMasterId: '',
            validFrom: todayCivilDate(),
          }

          // この side の他行で使用済みの bgMasterId
          const usedIdsExcludingThisRow = new Set(
            sideIndices
              .filter((i) => i !== idx)
              .map((i) => watchedPartnerBgs[i]?.bgMasterId ?? '')
              .filter((id) => id !== ''),
          )

          /** この行向けの BG オプション */
          const bgOptsForRow = availableOptions(
            bgCandidates,
            usedIdsExcludingThisRow,
            entry.bgMasterId,
          )

          /**
           * fallback option 用ラベル算出 (AC-MOI-023、occto-master-fk-as-of-filter
           * 設計書 Decision 2)。 対象日時点で終了していれば「（終了）」サフィックス
           * 付き、 物理削除済みなら「(削除済み)」を返す。
           */
          const fallbackLabel = resolveExpiryFallbackLabel(
            allBgMasters,
            entry.bgMasterId,
            watchedRecordValidFrom,
            {
              idOf: (bg) => bg.id,
              labelOf: (bg) => `${bg.bgCode} - ${bg.bgName}`,
              validTo: (bg) => bg.validTo,
              deletedLabel: t(
                'page:adminOcctoMaster.common.masterFallback.deleted',
              ),
              expiredSuffix: t(
                'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
              ),
            },
          )

          const bgError = partnerBgErrors?.[idx]?.bgMasterId

          return (
            <Box
              key={fields[idx]?.id ?? idx}
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* BG select + 削除ボタン */}
                <HStack align="flex-end" gap={3}>
                  <Box flex={1}>
                    <FieldRoot invalid={bgError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.partnerBgLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        control={control}
                        /* 既選択値が候補に無い場合のフォールバック表示。
                           絞り込み前の全 BG マスタ (allBgMasters) に存在すれば
                           `{bgCode} - {bgName}` を表示し (AC-06、有効期間外で
                           候補から外れた実在 BG)、無ければ「(削除済み)」を表示
                           する (= 物理削除された値のみ)。 未選択 ('') は
                           placeholder に戻す。 */
                        fallbackLabel={(v) =>
                          v === '' ? undefined : fallbackLabel
                        }
                        name={`partnerBgs.${idx}.bgMasterId`}
                        options={bgOptsForRow.map((bg) => ({
                          value: bg.id,
                          label: `${bg.bgCode} - ${bg.bgName}`,
                        }))}
                        placeholder={t(`page:${i18nNs}.form.bgUnselected`)}
                      />
                      {bgError?.message !== undefined && (
                        <FieldErrorText>{bgError.message}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  <OutlineDangerButton
                    flexShrink={0}
                    aria-label={t(removeButtonKey)}
                    onClick={() => {
                      remove(idx)
                    }}
                    size="sm"
                  >
                    {t(removeButtonKey)}
                  </OutlineDangerButton>
                </HStack>

                {/* 適用期間 */}
                <PeriodFields
                  basePath={`partnerBgs.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs={i18nNs}
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

export const SuppressionPartnerBgsSection = ({
  control,
  i18nNs,
  partnerBgs,
  allBgMasters,
  selfAreaCode,
}: Props): React.ReactElement => {
  // useFieldArray は 1 つだけ所有する (F5: dual instance anti-pattern 解消)
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partnerBgs',
  })

  // partnerBgs 全体を watch して side でフィルタした index を管理する
  const rawWatchedPartnerBgs = useWatch({ control, name: 'partnerBgs' }) as
    | PartnerBgEntry[]
    | undefined
  const watchedPartnerBgs = rawWatchedPartnerBgs ?? []

  // form 全体の validFrom (= レコードの適用開始日) を購読する (AC-03、
  // 4526-plan-master-valid-period-filter 設計書 §4 Q3-1)。
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | CivilDate
    | undefined

  /**
   * area + 有効期間 (isActiveOn) narrow 済み 取引先BG 候補 (全区分は集約 RPC
   * が保証済み)。 validFrom が transient に undefined の間は有効期間 narrow を
   * skip する (fail-open)。
   */
  const bgCandidates = useMemo(
    () =>
      partnerBgs.filter(
        (bg) =>
          (selfAreaCode === undefined || bg.areaCode === selfAreaCode) &&
          (watchedRecordValidFrom === undefined ||
            isActiveOn(bg.validFrom, bg.validTo, watchedRecordValidFrom)),
      ),
    [partnerBgs, selfAreaCode, watchedRecordValidFrom],
  )

  return (
    <Stack gap={4}>
      {/* 調達 side */}
      <SideSection
        allBgMasters={allBgMasters}
        append={append}
        bgCandidates={bgCandidates}
        control={control}
        fields={fields}
        i18nNs={i18nNs}
        remove={remove}
        side={DemandSuppressionPartnerSide.PROCUREMENT}
        watchedPartnerBgs={watchedPartnerBgs}
        watchedRecordValidFrom={watchedRecordValidFrom}
      />
      {/* 販売 side */}
      <SideSection
        allBgMasters={allBgMasters}
        append={append}
        bgCandidates={bgCandidates}
        control={control}
        fields={fields}
        i18nNs={i18nNs}
        remove={remove}
        side={DemandSuppressionPartnerSide.SALES}
        watchedPartnerBgs={watchedPartnerBgs}
        watchedRecordValidFrom={watchedRecordValidFrom}
      />
    </Stack>
  )
}
